import React, { useMemo } from "react";
import { View, ScrollView, Pressable, type ViewStyle } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import {
  ArrowUpRight,
  Plus,
  Syringe,
  Pipette,
  Snowflake,
  Baby,
  Wind,
  CircleDotDashed,
  ChevronLeft,
} from "lucide-react-native";
import { Text } from "@louise/ui/Text/Text";
import { useCycles, type Cycle } from "@src/packages/cycles/hooks/useCycles";
import { ContentContainer } from "@src/components/layout/ContentContainer";
import { useMedicalHistoryTheme } from "../../theme/MedicalHistoryTheme";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
type Treatment = {
  type: string;
  name?: string | null;
  date?: string | null;
  notes?: string | null;
  provider?: string | null;
  embryosFrozen?: number | null;
};

function getTreatmentIcon(type: string) {
  switch (type) {
    case "ivf":
      return Syringe;
    case "iui":
      return Pipette;
    case "fet":
      return Baby;
    case "meds_only":
      return Wind;
    case "egg_freezing":
    case "embryo_freezing":
    case "sperm_freezing":
      return Snowflake;
    default:
      return CircleDotDashed;
  }
}

function getTreatmentColor(
  type: string,
  theme: ReturnType<typeof useMedicalHistoryTheme>,
  t: TranslationFunction,
) {
  const baseConfig = {
    iconColor: theme.textFaint,
    borderColor: theme.inputBorder,
    actionButtons: [] as string[],
  };

  switch (type) {
    case "ivf":
      return {
        ...baseConfig,
        borderRotation: 45,
        borderType: "t-r",
        actionButtons: [
          t("med_history.fertility.past_cycles.action.embryo_details"),
          t("med_history.fertility.past_cycles.action.exam"),
        ],
      };
    case "iui":
      return {
        ...baseConfig,
        borderRotation: -12,
        borderType: "l-b",
        actionButtons: [
          t("med_history.fertility.past_cycles.action.medications"),
          t("med_history.fertility.past_cycles.action.exams"),
        ],
      };
    case "fet":
      return {
        ...baseConfig,
        borderRotation: 45,
        borderType: "r-b",
        actionButtons: [
          t("med_history.fertility.past_cycles.action.transfer"),
          t("med_history.fertility.past_cycles.action.document"),
        ],
      };
    case "meds_only":
      return {
        ...baseConfig,
        borderRotation: 0,
        borderType: "t",
        actionButtons: [
          t("med_history.fertility.past_cycles.action.sperm_source"),
          t("med_history.fertility.past_cycles.action.document"),
        ],
      };
    case "egg_freezing":
      return {
        ...baseConfig,
        borderRotation: 90,
        borderType: "t-l",
        actionButtons: [
          t("med_history.fertility.past_cycles.action.egg_details"),
          t("med_history.fertility.past_cycles.action.document"),
        ],
      };
    case "embryo_freezing":
      return {
        ...baseConfig,
        borderRotation: 90,
        borderType: "t-l",
        actionButtons: ["embryo details", "document"],
      };
    case "sperm_freezing":
      return {
        ...baseConfig,
        borderRotation: -12,
        borderType: "l-b",
        actionButtons: ["sperm details", "document"],
      };
    default:
      return {
        ...baseConfig,
        borderRotation: 0,
        borderType: "all",
        actionButtons: [t("med_history.fertility.past_cycles.action.details")],
      };
  }
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}.${year}`;
  } catch {
    return "";
  }
}

function parseNotes(notes: string | null | undefined): {
  clinic?: string;
  outcome?: string;
  eggsFrozen?: string;
  embryosFrozen?: string;
} {
  if (!notes) return {};
  const parts = notes.split(";").map((p) => p.trim());
  const result: {
    clinic?: string;
    outcome?: string;
    eggsFrozen?: string;
    embryosFrozen?: string;
  } = {};
  parts.forEach((part) => {
    const clinicMatch = part.match(/^clinic\s*:\s*(.*)$/i);
    const outcomeMatch = part.match(/^outcome\s*:\s*(.*)$/i);
    const eggsFrozenMatch = part.match(/^eggs_frozen\s*:\s*(.*)$/i);
    const embryosFrozenMatch = part.match(/^embryos_frozen\s*:\s*(.*)$/i);

    if (clinicMatch) {
      result.clinic = clinicMatch[1];
    } else if (outcomeMatch) {
      result.outcome = outcomeMatch[1];
    } else if (eggsFrozenMatch) {
      result.eggsFrozen = eggsFrozenMatch[1];
    } else if (embryosFrozenMatch) {
      result.embryosFrozen = embryosFrozenMatch[1];
    }
  });
  return result;
}

function findFrozenEmbryoMetric(cycle: Cycle) {
  for (const phase of cycle.phases ?? []) {
    const metric = phase.fertilityMetrics?.find(
      (entry) => entry.type === "frozen_embryo",
    );
    if (metric) return metric.value;
  }
  return null;
}

function getTreatmentLabel(
  type: string,
  name: string | null | undefined,
  t: TranslationFunction,
): string {
  const labelKeyMap: Record<string, string> = {
    ivf: "med_history.fertility.past_cycles.treatment.ivf",
    iui: "med_history.fertility.past_cycles.treatment.iui",
    fet: "med_history.fertility.past_cycles.treatment.fet",
    meds_only: "med_history.fertility.past_cycles.treatment.meds_only",
    egg_freezing: "med_history.fertility.past_cycles.treatment.egg_freezing",
    embryo_freezing:
      "med_history.fertility.past_cycles.treatment.embryo_freezing",
    sperm_freezing:
      "med_history.fertility.past_cycles.treatment.sperm_freezing",
    other: "med_history.fertility.past_cycles.treatment.other",
  };
  const labelKey =
    labelKeyMap[type] ?? "med_history.fertility.past_cycles.treatment.default";
  const base = t(labelKey);
  if (name) {
    return `${base} \u2022 ${name}`;
  }
  return base;
}

export function PastCyclesScreen() {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicalHistoryPastCyclesScreen");
  const navigation = useNavigation();
  const { data: cycles = [] } = useCycles();
  const theme = useMedicalHistoryTheme();

  // Helper function to transform cycle data to frontend treatment format
  const transformCycleToTreatment = (cycle: Cycle): Treatment => {
    const typeMap: Record<string, string> = {
      ivf: "ivf",
      iui: "iui",
      fet: "fet",
      icsi: "icsi",
      oi: "meds_only",
      egg_freezing: "egg_freezing",
      embryo_freezing: "embryo_freezing",
      sperm_freezing: "sperm_freezing",
    };

    const frontendType = typeMap[cycle.cycleType] || cycle.cycleType;

    const notesParts: string[] = [];
    if (cycle.outcome?.type) {
      const outcomeMap: Record<string, string> = {
        miscarriage: "miscarriage",
        pregnancy: "live_birth",
        no_pregnancy: "no_pregnancy",
        cancelled: "cancelled",
        other: "other",
      };
      const outcomeStr = outcomeMap[cycle.outcome.type] || cycle.outcome.type;
      notesParts.push(`outcome: ${outcomeStr}`);
    }
    if (cycle.outcome?.notes) {
      notesParts.push(cycle.outcome.notes);
    }
    if (cycle.dayNotes && cycle.dayNotes.length > 0) {
      const dayNotesText = cycle.dayNotes
        .map((dn) => dn.notes)
        .filter(Boolean)
        .join("; ");
      if (dayNotesText) {
        notesParts.push(dayNotesText);
      }
    }

    const legacyFrozenCount = Number.parseInt(
      parseNotes(notesParts.join("; ")).embryosFrozen ?? "",
      10,
    );

    return {
      type: frontendType,
      name: cycle.description || null,
      date: cycle.startDate || null,
      notes: notesParts.length > 0 ? notesParts.join("; ") : null,
      provider: cycle.provider,
      embryosFrozen:
        findFrozenEmbryoMetric(cycle) ??
        (Number.isInteger(legacyFrozenCount) ? legacyFrozenCount : null),
    };
  };

  const treatments = useMemo(() => {
    const pastCycles = cycles.filter(
      (c) => c.status === "closed" && c.cycleType,
    );
    return pastCycles.map(transformCycleToTreatment);
  }, [cycles]);

  const cycleCount = treatments.length;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: theme.screenBg }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 128 }}
        showsVerticalScrollIndicator={false}
      >
        <ContentContainer>
          {/* Navigation Header */}
          <View
            style={{
              position: "relative",
              marginBottom: 24,
              width: "100%",
              paddingHorizontal: 24,
              paddingTop: 8,
            }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("med_history.overview.back")}
              onPress={() => {
                // Chevron-only control; its accessibility label is the copy.
                capturePress(
                  pressLabel(t("med_history.overview.back")),
                  "med_history.overview.back",
                );
                navigation.goBack();
              }}
              style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
            >
              <View
                style={{
                  height: 32,
                  width: 32,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 16,
                  borderWidth: 1,
                  borderColor: theme.inputBorder,
                  backgroundColor: theme.iconBg,
                }}
              >
                <ChevronLeft
                  size={20}
                  color={theme.textMuted}
                  strokeWidth={1.5}
                />
              </View>
              <Text
                variant="body"
                size="base"
                style={{
                  fontSize: 14.4,
                  fontWeight: "500",
                  letterSpacing: -0.3,
                  color: theme.textMuted,
                }}
              >
                {t("med_history.fertility.past_cycles.back")}
              </Text>
            </Pressable>
          </View>

          {/* Section 1: Past Cycles */}
          <View style={{ marginBottom: 32, gap: 16, paddingHorizontal: 24 }}>
            {/* Section Header */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 12 }}
              >
                <Text
                  variant="display"
                  size="lg"
                  style={{
                    fontSize: 20,
                    fontWeight: "600",
                    letterSpacing: -0.3,
                    color: theme.text,
                  }}
                >
                  {t("med_history.fertility.past_cycles.title")}
                </Text>
                <View
                  style={{
                    height: 24,
                    width: 24,
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: `${theme.accentLight}33`,
                    backgroundColor: `${theme.accentLight}1A`,
                  }}
                >
                  <Text
                    variant="caption"
                    size="caps"
                    style={{
                      fontSize: 12,
                      fontWeight: "600",
                      color: theme.accentLight,
                    }}
                  >
                    {cycleCount}
                  </Text>
                </View>
              </View>
              <Pressable
                style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
              >
                <Plus size={14} color={theme.textFaint} />
                <Text
                  variant="caption"
                  size="standard"
                  style={{
                    fontSize: 12,
                    fontWeight: "600",
                    color: theme.textFaint,
                  }}
                >
                  {t("med_history.fertility.past_cycles.add_cycle")}
                </Text>
              </Pressable>
            </View>

            {/* Cards Container */}
            <View style={{ gap: 12 }}>
              {treatments.map((treatment, index) => {
                const Icon = getTreatmentIcon(treatment.type);
                const colorConfig = getTreatmentColor(treatment.type, theme, t);
                const label = getTreatmentLabel(
                  treatment.type,
                  treatment.name,
                  t,
                );
                const parsedNotes = parseNotes(treatment.notes);
                const clinic = treatment.provider ?? parsedNotes.clinic;
                const dateStr = formatDate(treatment.date);
                const frozenCount =
                  treatment.embryosFrozen ??
                  Number.parseInt(parsedNotes.embryosFrozen ?? "", 10);

                const getBorderStyle = () => {
                  const baseStyle: ViewStyle = {
                    position: "absolute",
                    inset: 0,
                    borderRadius: 22,
                  };
                  const rotation = colorConfig.borderRotation;

                  switch (colorConfig.borderType) {
                    case "t-r":
                      return {
                        ...baseStyle,
                        borderTopWidth: 1,
                        borderRightWidth: 1,
                        borderColor: colorConfig.borderColor,
                        transform: [{ rotate: `${rotation}deg` }],
                      };
                    case "l-b":
                      return {
                        ...baseStyle,
                        borderLeftWidth: 1,
                        borderBottomWidth: 1,
                        borderColor: colorConfig.borderColor,
                        transform: [{ rotate: `${rotation}deg` }],
                      };
                    case "r-b":
                      return {
                        ...baseStyle,
                        borderRightWidth: 1,
                        borderBottomWidth: 1,
                        borderColor: colorConfig.borderColor,
                        transform: [{ rotate: `${rotation}deg` }],
                      };
                    case "t-l":
                      return {
                        ...baseStyle,
                        borderTopWidth: 1,
                        borderLeftWidth: 1,
                        borderColor: colorConfig.borderColor,
                        transform: [{ rotate: `${rotation}deg` }],
                      };
                    case "t":
                      return {
                        ...baseStyle,
                        borderTopWidth: 1,
                        borderColor: colorConfig.borderColor,
                        transform: [{ rotate: `${rotation}deg` }],
                      };
                    default:
                      return {
                        ...baseStyle,
                        borderWidth: 1,
                        borderColor: colorConfig.borderColor,
                      };
                  }
                };

                return (
                  <View
                    key={index}
                    style={{
                      borderRadius: 16,
                      borderWidth: 1,
                      borderColor: theme.iconBorder,
                      backgroundColor: theme.optionBg,
                      padding: 16,
                    }}
                  >
                    <View
                      style={{
                        marginBottom: 12,
                        flexDirection: "row",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <View style={{ flexDirection: "row", gap: 16 }}>
                        {/* Icon Ring */}
                        <View
                          style={{
                            position: "relative",
                            height: 44,
                            width: 44,
                            flexShrink: 0,
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 22,
                          }}
                        >
                          <View
                            style={{
                              position: "absolute",
                              width: 44,
                              height: 44,
                              borderRadius: 22,
                              borderWidth: 1,
                              borderColor: theme.inputBorder,
                            }}
                          />
                          <View
                            style={{
                              position: "absolute",
                              width: 44,
                              height: 44,
                              borderRadius: 22,
                              backgroundColor: theme.optionBg,
                            }}
                          />
                          <View style={getBorderStyle()} />
                          <Icon
                            size={20}
                            color={colorConfig.iconColor}
                            strokeWidth={1.5}
                          />
                        </View>
                        <View>
                          <Text
                            variant="body"
                            size="base"
                            style={{
                              marginBottom: 8,
                              fontSize: 16,
                              fontWeight: "700",
                              color: theme.text,
                            }}
                          >
                            {label}
                          </Text>
                          <View
                            style={{
                              flexDirection: "row",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            {clinic && (
                              <View
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 4,
                                  borderRadius: 6,
                                  borderWidth: 1,
                                  borderColor: theme.iconBorder,
                                  backgroundColor: theme.iconBg,
                                  paddingHorizontal: 8,
                                  paddingVertical: 2,
                                }}
                              >
                                <Text
                                  variant="caption"
                                  size="standard"
                                  style={{
                                    fontSize: 10.4,
                                    color: theme.textMuted,
                                  }}
                                >
                                  {clinic}
                                </Text>
                              </View>
                            )}
                            {dateStr && (
                              <View
                                style={{
                                  alignItems: "center",
                                  borderRadius: 6,
                                  borderWidth: 1,
                                  borderColor: theme.iconBorder,
                                  backgroundColor: theme.iconBg,
                                  paddingHorizontal: 8,
                                  paddingVertical: 2,
                                }}
                              >
                                <Text
                                  variant="caption"
                                  size="standard"
                                  style={{
                                    fontSize: 10.4,
                                    color: theme.textMuted,
                                  }}
                                >
                                  {dateStr}
                                </Text>
                              </View>
                            )}
                            {treatment.type === "embryo_freezing" &&
                            Number.isInteger(frozenCount) ? (
                              <View
                                style={{
                                  alignItems: "center",
                                  borderRadius: 6,
                                  borderWidth: 1,
                                  borderColor: theme.iconBorder,
                                  backgroundColor: theme.iconBg,
                                  paddingHorizontal: 8,
                                  paddingVertical: 2,
                                }}
                              >
                                <Text
                                  variant="caption"
                                  size="standard"
                                  style={{
                                    fontSize: 10.4,
                                    color: theme.textMuted,
                                  }}
                                >
                                  {t("care.past_cycle_detail.embryos_frozen", {
                                    params: { count: String(frozenCount) },
                                  })}
                                </Text>
                              </View>
                            ) : null}
                          </View>
                        </View>
                      </View>
                      <ArrowUpRight size={16} color={theme.inputPlaceholder} />
                    </View>
                    {/* Action buttons */}
                    <View style={{ flexDirection: "row", gap: 8 }}>
                      {colorConfig.actionButtons?.map(
                        (buttonText, btnIndex) => (
                          <Pressable
                            key={btnIndex}
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 4,
                              borderRadius: 8,
                              borderWidth: 1,
                              borderColor: theme.inputBorder,
                              backgroundColor: "transparent",
                              paddingHorizontal: 12,
                              paddingVertical: 6,
                            }}
                          >
                            <Plus size={10} color={theme.textFaint} />
                            <Text
                              variant="caption"
                              size="standard"
                              style={{
                                fontSize: 11.2,
                                fontWeight: "500",
                                color: theme.textFaint,
                              }}
                            >
                              {buttonText}
                            </Text>
                          </Pressable>
                        ),
                      )}
                    </View>
                  </View>
                );
              })}

              {treatments.length === 0 && (
                <View
                  style={{
                    alignItems: "center",
                    borderRadius: 16,
                    borderWidth: 1,
                    borderColor: theme.inputBorder,
                    backgroundColor: theme.iconBg,
                    padding: 32,
                  }}
                >
                  <Text
                    variant="body"
                    size="base"
                    style={{ color: theme.textFaint }}
                  >
                    {t("med_history.fertility.past_cycles.empty")}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </ContentContainer>
      </ScrollView>
    </SafeAreaView>
  );
}

export default PastCyclesScreen;
